import React from 'react';
import { Button } from './Button';

// Small shared parts of a list page's filters (DESIGN.md §9.2–9.4).

// DisplayInactiveToggle (V2-075; INACT-01) - for records that are simply active or inactive.
// Off by default: inactive records are hidden. Never shown alongside a Status dropdown for the
// same thing (INACT-03).
export interface DisplayInactiveToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  id?: string;
}

export const DisplayInactiveToggle: React.FC<DisplayInactiveToggleProps> = ({ checked, onChange, id = 'display-inactive' }) => (
  <label htmlFor={id} className="tw-flex tw-min-h-[2.25rem] tw-cursor-pointer tw-items-center tw-gap-2 tw-text-sm tw-text-content">
    <input
      id={id}
      type="checkbox"
      role="switch"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="tw-h-4 tw-w-4 tw-rounded tw-border-line-input tw-text-primary"
    />
    Display Inactive
  </label>
);

// HiddenMatchHint (V2-076; SRCH-05) - when a search finds fewer records than it would without
// the filters, say so above the results and offer to show them in one click:
//   "No active employees match 'Aaron Pazur'. 1 inactive match – Show"
export interface HiddenMatchHintProps {
  /** The search text. */
  search: string;
  /** How many results the filtered search returned. */
  shownCount: number;
  /** How many the same search returns without the filters. */
  unfilteredCount: number;
  /** Plural noun for the records, already qualified by the hiding filter: "active employees". */
  shownNoun: string;
  /** Singular / plural noun for the hidden ones: ["inactive match", "inactive matches"]. */
  hiddenNoun?: [string, string];
  /** Shows the hidden matches (turns Display Inactive on, or clears the filters that hid them). */
  onShow: () => void;
}

export const HiddenMatchHint: React.FC<HiddenMatchHintProps> = ({
  search,
  shownCount,
  unfilteredCount,
  shownNoun,
  hiddenNoun = ['match hidden by the filters', 'matches hidden by the filters'],
  onShow,
}) => {
  const hidden = unfilteredCount - shownCount;
  if (!search.trim() || hidden <= 0) return null;
  const lead = shownCount === 0 ? `No ${shownNoun} match '${search.trim()}'. ` : '';
  return (
    <p className="tw-mb-2 tw-flex tw-flex-wrap tw-items-center tw-gap-x-1 tw-text-sm tw-text-content-secondary" role="status" data-testid="hidden-match-hint">
      <span>
        {lead}
        {hidden} {hidden === 1 ? hiddenNoun[0] : hiddenNoun[1]} –
      </span>
      <Button type="button" variant="link" size="sm" className="!tw-p-0 tw-align-baseline" onClick={onShow}>
        Show
      </Button>
    </p>
  );
};
